import { useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  CONVERSATION_ISSUE_SUMMARY, IMPROVABLE_AGENTS, IMPROVEMENT_RUNS, KNOWN_AGENTS,
  LIST_CONVERSATION_ISSUES, PROMPT_VERSIONS, PROMPT_VERSION_CONTENT, SELF_IMPROVEMENT_STATUS,
} from '../queries/agentQuality';
import {
  ACTIVATE_PROMPT_VERSION, IMPROVE_ONLINE_AGENT, MARK_ISSUE_FIXED, MARK_ISSUE_REVIEWED,
  MARK_ISSUE_WONT_FIX, TRIGGER_PROMPT_IMPROVEMENT, VALIDATE_PROMPT_VERSIONS,
} from '../mutations/agentQuality';

const READ = { fetchPolicy: 'cache-and-network', errorPolicy: 'all' };

/** Severities, worst first — the order the triage list is read in. */
export const SEVERITIES = ['high', 'medium', 'low'];

/**
 * The issue categories the analyser files things under, with readable names.
 *
 * Kept as a list rather than read from the server because the platform exposes
 * no enum for it — the field is a plain String. An unrecognised value still
 * renders, de-underscored, so a new category added upstream appears rather
 * than vanishing.
 */
export const ISSUE_CATEGORIES = {
  drift_to_prose: 'Drift to prose',
  wrong_navigation: 'Wrong navigation',
  missed_navigation: 'Missed navigation',
  wrong_tool_choice: 'Wrong tool choice',
  hallucinated_number: 'Hallucinated number',
  repeated_question: 'Repeated question',
  tool_failure_unrecovered: 'Tool failure unrecovered',
  ignored_user_intent: 'Ignored user intent',
  missing_feature: 'Missing feature',
  tool_data_gap: 'Tool / data gap',
};

/** The statuses an issue can be filtered to. */
export const ISSUE_STATUSES = [['open', 'Open'], ['fixed', 'Fixed'], ['wont_fix', "Won't fix"]];

/**
 * The filter form, empty.
 *
 * `status: 'open'` is the one non-blank default, and deliberately so: the
 * screen exists to work through what has not been dealt with.
 */
export const EMPTY_ISSUE_FILTERS = {
  agentName: '',
  category: '',
  minSeverity: '',
  status: 'open',
  reviewed: '',
  promoted: '',
  threadName: '',
  userId: '',
  sinceDays: '',
  limit: '',
};

/**
 * Form values → query variables, dropping blanks.
 *
 * A blank must become `undefined` rather than null or '': the resolver treats a
 * null `reviewed` as "either" but an empty string as a type error, and a
 * `limit` of 0 would ask for nothing.
 */
export const issueFilterVars = (f) => {
  const str = (v) => (v && String(v).trim() ? String(v).trim() : undefined);
  const bool = (v) => (v === '' || v == null ? undefined : v === 'true');
  const int = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : undefined;
  };
  return {
    agentName: str(f.agentName),
    category: str(f.category),
    minSeverity: str(f.minSeverity),
    status: str(f.status),
    reviewed: bool(f.reviewed),
    promoted: bool(f.promoted),
    threadName: str(f.threadName),
    userId: str(f.userId),
    sinceDays: int(f.sinceDays),
    limit: int(f.limit) ?? 100,
  };
};
export const SEVERITY_TONE = { high: 'red', medium: 'amber', low: 'grey' };
export const ISSUE_STATUS_TONE = {
  open: 'amber', fixed: 'green', wont_fix: 'grey', reviewed: 'blue',
};
/** How long back the triage screens look, in hours and days respectively. */
export const ISSUE_WINDOWS = [['24h', 24], ['7 days', 168], ['30 days', 720]];

/** Issues rolled up per agent and category. */
export function useIssueSummary({ agentName = null, sinceHours = 168, onlyUnreviewed = false } = {}) {
  const { data, loading, error, refetch } = useQuery(CONVERSATION_ISSUE_SUMMARY, {
    variables: {
      agentName, sinceHours, onlyUnreviewed, onlyUnpromoted: false, status: null, limit: 200,
    },
    ...READ,
  });
  return useMemo(() => ({
    rows: data?.conversationIssueSummary ?? [],
    loading,
    error,
    refetch,
  }), [data, loading, error, refetch]);
}

/**
 * The issues themselves.
 *
 * Takes the variables as given — `issueFilterVars` has already dropped the
 * blanks — so the screen decides what it is asking for and this does not
 * quietly re-add defaults behind it. `limit` is the one guaranteed field.
 */
export function useConversationIssues(vars = {}) {
  const { data, loading, error, refetch } = useQuery(LIST_CONVERSATION_ISSUES, {
    variables: { limit: 100, ...vars },
    /* network-only: after triaging one issue the list must not answer from a
       cache that still has it open. */
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });
  return useMemo(() => ({
    rows: data?.listConversationIssues ?? [],
    loading,
    error,
    refetch,
  }), [data, loading, error, refetch]);
}

export function useKnownAgents({ sinceHours = 168 } = {}) {
  const { data, loading, error, refetch } = useQuery(KNOWN_AGENTS, {
    variables: { sinceHours }, ...READ,
  });
  return { rows: data?.knownAgents ?? [], loading, error, refetch };
}

/** The agents whose prompts can be improved — the enum values, as strings. */
export function useImprovableAgents() {
  const { data, loading, error } = useQuery(IMPROVABLE_AGENTS, READ);
  return { names: data?.improvableAgents ?? [], loading, error };
}

export function useSelfImprovementStatus(agentNames = null) {
  const { data, loading, error, refetch } = useQuery(SELF_IMPROVEMENT_STATUS, {
    variables: { agentNames }, ...READ,
  });
  return { rows: data?.selfImprovementStatus ?? [], loading, error, refetch };
}

export function usePromptVersions(agentName, { limit = 25 } = {}) {
  const { data, loading, error, refetch } = useQuery(PROMPT_VERSIONS, {
    variables: { agentName, limit },
    skip: !agentName,
    ...READ,
  });
  return { rows: data?.promptVersions ?? [], loading, error, refetch };
}

export function usePromptVersionContent(versionId) {
  const { data, loading, error } = useQuery(PROMPT_VERSION_CONTENT, {
    variables: { versionId: Number(versionId) },
    skip: versionId == null,
    ...READ,
  });
  return { version: data?.promptVersionContent ?? null, loading, error };
}

export function useImprovementRuns(agentName, { limit = 10 } = {}) {
  const { data, loading, error, refetch } = useQuery(IMPROVEMENT_RUNS, {
    variables: { agentName, limit },
    skip: !agentName,
    ...READ,
  });
  return { rows: data?.improvementRuns ?? [], loading, error, refetch };
}

/* Triage refreshes both the list and the roll-up, or the counts go stale. */
const TRIAGE = { refetchQueries: ['ListConversationIssues', 'ConversationIssueSummary', 'KnownAgents'] };

export const useMarkIssueFixed = () => useMutation(MARK_ISSUE_FIXED, TRIAGE);
export const useMarkIssueWontFix = () => useMutation(MARK_ISSUE_WONT_FIX, TRIAGE);
export const useMarkIssueReviewed = () => useMutation(MARK_ISSUE_REVIEWED, TRIAGE);

export const useTriggerPromptImprovement = () => useMutation(TRIGGER_PROMPT_IMPROVEMENT, {
  refetchQueries: ['PromptVersions', 'ImprovementRuns', 'SelfImprovementStatus'],
});
export const useActivatePromptVersion = () => useMutation(ACTIVATE_PROMPT_VERSION, {
  refetchQueries: ['PromptVersions', 'SelfImprovementStatus'],
});

/**
 * Run the analysis that finds issues.
 *
 * `apply` is pinned false at the call site — see the document. Everything the
 * pass produces has to be re-read afterwards, and it runs in the background,
 * so this refreshes the issue lists and the runs rather than reporting a result.
 */
export const useRunAgentAnalysis = () => useMutation(IMPROVE_ONLINE_AGENT, {
  refetchQueries: [
    'ListConversationIssues', 'ConversationIssueSummary', 'KnownAgents',
    'ImprovementRuns', 'PromptVersions', 'SelfImprovementStatus',
  ],
});

export const useValidatePromptVersions = () => useMutation(VALIDATE_PROMPT_VERSIONS, {
  refetchQueries: ['PromptVersions', 'SelfImprovementStatus'],
});
